import React, { useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import {
  Build as BuildIcon,
  NotificationsActive as AlertsIcon,
  TableChart as TableIcon,
  History as HistoryIcon,
  Receipt as RecordsIcon,
  Add as AddIcon,
  LocalShipping as VehiclesIcon,
  RvHookup as TrailersIcon,
} from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import {
  MaintenanceRecordDto,
  TrailerMaintenanceRecordDto,
  useGetMaintenanceDataQuery,
  useGetMaintenanceAlertsQuery,
  useGetMaintenanceAlertHistoryQuery,
  useGetTrailerMaintenanceRecordsQuery,
} from "../../api/maintenanceApi";
import { useGetTrailersQuery } from "../../api/trailersApi";
import PageLayout from "../../components/layout/PageLayout";
import PageTitle from "../../components/common/PageTitle";
import TabbedPageCard from "../../components/common/TabbedPageCard";
import AlertsBoard from "./components/AlertsBoard";
import MaintenanceTable from "./components/MaintenanceTable";
import MaintenanceEditDialog from "./components/MaintenanceEditDialog";
import MaintenanceRecordDialog from "./components/MaintenanceRecordDialog";
import MaintenanceRecordsTable from "./components/MaintenanceRecordsTable";
import TrailerMaintenanceRecordDialog from "./components/TrailerMaintenanceRecordDialog";
import TrailerMaintenanceRecordsTable from "./components/TrailerMaintenanceRecordsTable";

type AssetMode = "vehicles" | "trailers";

const MaintenancePage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const assetMode: AssetMode = location.pathname.includes("/trailers")
    ? "trailers"
    : "vehicles";

  const {
    data: maintenanceData,
    isLoading: isLoadingData,
    isError: isDataError,
  } = useGetMaintenanceDataQuery(undefined, { skip: assetMode !== "vehicles" });
  const { data: alerts = [], isLoading: isLoadingAlerts } =
    useGetMaintenanceAlertsQuery(undefined, { skip: assetMode !== "vehicles" });
  const { data: alertHistory = [], isLoading: isLoadingHistory } =
    useGetMaintenanceAlertHistoryQuery(undefined, {
      skip: assetMode !== "vehicles",
    });
  const { data: trailers = [] } = useGetTrailersQuery(undefined, {
    skip: assetMode !== "trailers",
  });
  const { data: trailerRecords = [] } = useGetTrailerMaintenanceRecordsQuery(
    undefined,
    { skip: assetMode !== "trailers" }
  );

  const [editVehicleId, setEditVehicleId] = useState<number | null>(null);
  const [recordDialogOpen, setRecordDialogOpen] = useState(false);
  const [recordPreselectedVehicleId, setRecordPreselectedVehicleId] =
    useState<number | null>(null);
  const [editingRecord, setEditingRecord] =
    useState<MaintenanceRecordDto | null>(null);

  const [trailerRecordDialogOpen, setTrailerRecordDialogOpen] = useState(false);
  const [editingTrailerRecord, setEditingTrailerRecord] =
    useState<TrailerMaintenanceRecordDto | null>(null);

  const trailerPickerOptions = useMemo(
    () =>
      trailers.filter(
        (t) =>
          !t.isArchived ||
          (editingTrailerRecord != null && t.id === editingTrailerRecord.trailerId)
      ),
    [trailers, editingTrailerRecord]
  );

  const editData =
    maintenanceData?.find((d) => d.vehicleId === editVehicleId) ?? null;

  const vehicleNames = useMemo(() => {
    if (!maintenanceData) return [];
    return [...new Set(maintenanceData.map((d) => d.vehicleName))].sort();
  }, [maintenanceData]);

  const trailerNames = useMemo(() => {
    return [...new Set(trailerRecords.map((r) => r.trailerName))].sort();
  }, [trailerRecords]);

  const handleAssetModeChange = (
    _: React.MouseEvent<HTMLElement>,
    next: AssetMode | null
  ) => {
    if (!next || next === assetMode) return;
    navigate(next === "trailers" ? "/maintenance/trailers" : "/maintenance");
  };

  const handleEditClose = () => {
    const closingVehicleId = editVehicleId;
    setEditVehicleId(null);
    if (closingVehicleId != null) {
      setRecordPreselectedVehicleId(closingVehicleId);
      setRecordDialogOpen(true);
    }
  };

  const handleOpenStandaloneRecord = () => {
    setRecordPreselectedVehicleId(null);
    setEditingRecord(null);
    setRecordDialogOpen(true);
  };

  const handleEditRecord = (record: MaintenanceRecordDto) => {
    setEditingRecord(record);
    setRecordPreselectedVehicleId(null);
    setRecordDialogOpen(true);
  };

  const handleRecordClose = () => {
    setRecordDialogOpen(false);
    setRecordPreselectedVehicleId(null);
    setEditingRecord(null);
  };

  const handleOpenTrailerRecord = () => {
    setEditingTrailerRecord(null);
    setTrailerRecordDialogOpen(true);
  };

  const handleEditTrailerRecord = (record: TrailerMaintenanceRecordDto) => {
    setEditingTrailerRecord(record);
    setTrailerRecordDialogOpen(true);
  };

  const handleTrailerRecordClose = () => {
    setTrailerRecordDialogOpen(false);
    setEditingTrailerRecord(null);
  };

  return (
    <PageLayout variant="fullHeight">
      <PageTitle title="Maintenance" icon={<BuildIcon />} />

      <Box sx={{ mb: 2 }}>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={assetMode}
          onChange={handleAssetModeChange}
          aria-label="Maintenance asset type"
        >
          <ToggleButton value="vehicles" aria-label="Vehicles">
            <VehiclesIcon sx={{ mr: 1, fontSize: 18 }} />
            Vehicles
          </ToggleButton>
          <ToggleButton value="trailers" aria-label="Trailers">
            <TrailersIcon sx={{ mr: 1, fontSize: 18 }} />
            Trailers
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {assetMode === "vehicles" && isDataError && (
        <Typography color="error">
          Failed to load maintenance data. Please try again.
        </Typography>
      )}

      {assetMode === "vehicles" && !isDataError && (
        <TabbedPageCard
          variant="flat"
          fullHeight
          idPrefix="maintenance-vehicles"
          ariaLabel="Vehicle maintenance tabs"
          tabs={[
            {
              id: "alerts",
              label: (
                <Badge
                  badgeContent={alerts.length}
                  color="error"
                  max={99}
                  sx={{ "& .MuiBadge-badge": { fontSize: "0.65rem" } }}
                >
                  Alerts
                </Badge>
              ),
              icon: <AlertsIcon />,
              iconPosition: "start",
              content: (
                <Box sx={{ pt: 2 }}>
                  <AlertsBoard alerts={alerts} isLoading={isLoadingAlerts} />
                </Box>
              ),
            },
            {
              id: "table",
              label: "Vehicles",
              icon: <TableIcon />,
              iconPosition: "start",
              content: (
                <Box sx={{ pt: 2 }}>
                  <MaintenanceTable
                    data={maintenanceData ?? []}
                    onEdit={setEditVehicleId}
                    isLoading={isLoadingData}
                  />
                </Box>
              ),
            },
            {
              id: "records",
              label: "Records",
              icon: <RecordsIcon />,
              iconPosition: "start",
              content: (
                <Box sx={{ pt: 2 }}>
                  <Box
                    sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}
                  >
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={handleOpenStandaloneRecord}
                    >
                      Add Record
                    </Button>
                  </Box>
                  <MaintenanceRecordsTable
                    vehicleNames={vehicleNames}
                    vehicles={maintenanceData ?? []}
                    onEdit={handleEditRecord}
                  />
                </Box>
              ),
            },
            {
              id: "history",
              label: (
                <Badge
                  badgeContent={alertHistory.length}
                  color="default"
                  max={99}
                  sx={{ "& .MuiBadge-badge": { fontSize: "0.65rem" } }}
                >
                  Alert History
                </Badge>
              ),
              icon: <HistoryIcon />,
              iconPosition: "start",
              content: (
                <Box sx={{ pt: 2 }}>
                  <AlertsBoard
                    alerts={alertHistory}
                    isLoading={isLoadingHistory}
                    showResolvedAt
                  />
                </Box>
              ),
            },
          ]}
        />
      )}

      {assetMode === "trailers" && (
        <Box sx={{ pt: 1 }}>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleOpenTrailerRecord}
            >
              Add Record
            </Button>
          </Box>
          <TrailerMaintenanceRecordsTable
            trailerNames={trailerNames}
            onEdit={handleEditTrailerRecord}
          />
        </Box>
      )}

      <MaintenanceEditDialog
        open={editVehicleId !== null}
        onClose={handleEditClose}
        data={editData}
      />

      <MaintenanceRecordDialog
        open={recordDialogOpen}
        onClose={handleRecordClose}
        vehicles={maintenanceData ?? []}
        preselectedVehicleId={recordPreselectedVehicleId}
        editRecord={editingRecord}
      />

      <TrailerMaintenanceRecordDialog
        open={trailerRecordDialogOpen}
        onClose={handleTrailerRecordClose}
        trailers={trailerPickerOptions}
        editRecord={editingTrailerRecord}
      />
    </PageLayout>
  );
};

export default MaintenancePage;
